import type { BlockedTargetingField, JsonSchema, TargetingRuleValidationError } from './types';

type ValidateTargetingRulesResult = {
  blockedFields: BlockedTargetingField[];
  isValid: boolean;
};

export type ValidateTargetingRules = (data: unknown) => ValidateTargetingRulesResult;

const validatorCache = new Map<string, Promise<ValidateTargetingRules>>();

export const clearTargetingRulesValidatorCache = (): void => {
  validatorCache.clear();
};

const getFieldFromInstancePath = (instancePath: string): string => instancePath.split('/').filter(Boolean).join('.');

const extractBlockedFieldMessages = (
  errors: TargetingRuleValidationError[] | null | undefined
): BlockedTargetingField[] => {
  if (!errors?.length) {
    return [];
  }

  const seenFields = new Set<string>();
  const blockedFields: BlockedTargetingField[] = [];

  errors.forEach(error => {
    const message = error.parentSchema?.['x-error-message'];

    if (typeof message !== 'string') {
      return;
    }

    const field = getFieldFromInstancePath(error.instancePath);

    if (!field || seenFields.has(field)) {
      return;
    }

    seenFields.add(field);
    blockedFields.push({
      field,
      message,
    });
  });

  return blockedFields;
};

export const createTargetingRulesValidator = (schema: JsonSchema): Promise<ValidateTargetingRules> => {
  const schemaCacheKey = JSON.stringify(schema);

  const cachedValidator = validatorCache.get(schemaCacheKey);

  if (cachedValidator) {
    return cachedValidator;
  }

  const nextValidatorPromise = (async () => {
    const { default: Ajv } = await import('ajv');

    const ajv = new Ajv({
      allErrors: true,
      strict: false,
      verbose: true,
    });

    ajv.addVocabulary(['x-error-message', 'x-tooltip-message']);

    const validate = ajv.compile(schema);

    return (data: unknown): ValidateTargetingRulesResult => {
      const isValid = validate(data);

      return {
        isValid,
        blockedFields: extractBlockedFieldMessages(
          validate.errors as TargetingRuleValidationError[] | null | undefined
        ),
      };
    };
  })();

  validatorCache.set(schemaCacheKey, nextValidatorPromise);

  return nextValidatorPromise;
};
